import { Utils } from "@/javascript/utils";
import { Box, Tooltip } from "@mantine/core";
import React, { useEffect, useRef, useState } from "react";

interface ResizablePaneProps {
	children: [React.ReactNode, React.ReactNode];
	initialPercentage?: number;
	minPercentage?: number;
	maxPercentage?: number;
	minPixelsForTop?: number;
	maxPixelsForTop?: number;
	overrideBottom: boolean;
}

const ResizablePane: React.FC<ResizablePaneProps> = ({
	children,
	initialPercentage = 50,
	minPercentage = 0,
	maxPercentage = 100,
	minPixelsForTop,
	maxPixelsForTop,
	overrideBottom
}) => {
	const [topPercentage, setTopPercentage] = useState(initialPercentage);
	const [isHovered, setIsHovered] = useState(false);
	const [hoverX, setHoverX] = useState(0);
	const isResizingRef = useRef(false);
	const isMovingRef = useRef(false);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const dividerRef = useRef<HTMLDivElement | null>(null);

	const interactHeight = 15;

	const handleMouseDown = () => {
		isResizingRef.current = true;
	};

	const handleMouseMove = (e: MouseEvent) => {
		if (isResizingRef.current && containerRef.current) {
			isMovingRef.current = true;
			const containerRect = containerRef.current.getBoundingClientRect();
			const containerHeight = containerRect.height;
			const relativeY = e.clientY - containerRect.top + interactHeight / 2;

			let newPercentage = (relativeY / containerHeight) * 100;
			const pixelHeight = (containerHeight * newPercentage) / 100;

			if (minPixelsForTop && pixelHeight < minPixelsForTop) {
				newPercentage = (minPixelsForTop / containerHeight) * 100;
			}

			if (maxPixelsForTop && pixelHeight > maxPixelsForTop) {
				newPercentage = (maxPixelsForTop / containerHeight) * 100;
			}

			setTopPercentage(Math.min(Math.max(newPercentage, minPercentage), maxPercentage));
		}
	};

	const handleSplitterClick = () => {
		if (!isMovingRef.current) {
			if (topPercentage !== initialPercentage) {
				setTopPercentage(initialPercentage);
			}
		}
	};

	const throttledMouseMove = Utils.throttle(handleMouseMove, 10, { leading: true, trailing: true });

	const handleMouseUp = () => {
		isResizingRef.current = false;
		setTimeout(() => {
			isMovingRef.current = false;
		}, 50);
	};

	useEffect(() => {
		document.addEventListener("mousemove", throttledMouseMove);
		document.addEventListener("mouseup", handleMouseUp);

		return () => {
			document.removeEventListener("mousemove", throttledMouseMove);
			document.removeEventListener("mouseup", handleMouseUp);
		};
	}, [throttledMouseMove]);

	return (
		<Box
			ref={containerRef}
			style={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				width: "100%",
				overflow: "hidden",
				position: "relative",
			}}
		>
			{(!overrideBottom) ? (
				<>
					{/* Top Pane */}
					<Box
						style={{
							overflow: "hidden",
							transition: isMovingRef.current ? "none" : "all 200ms ease-out",
							height: `${topPercentage}%`
						}}
					>
						{children[0]}
					</Box>

					{/* Divider with larger invisible area and thin visible line */}
					<Box
						ref={dividerRef}
						style={{
							top: -interactHeight / 2,
							height: interactHeight, // Taller interactive area
							width: "100%",
							position: "relative",
							cursor: "row-resize",
							userSelect: "none",
							zIndex: 5
						}}
						onMouseDown={handleMouseDown}
						onMouseEnter={() => setIsHovered(true)}
						onMouseLeave={() => setIsHovered(false)}
						onMouseMove={(e) => {
							if (dividerRef.current && !isHovered && !isMovingRef.current) {
								const rect = dividerRef.current.getBoundingClientRect();
								setHoverX(e.clientX - rect.left);
							}
						}}
						onClick={handleSplitterClick}
					>
						{/* Visible thin divider line */}
						<Box
							style={{
								position: "absolute",
								top: "50%",
								left: 0,
								right: 0,
								height: "2px", // Thin visual line
								backgroundColor: "#ccc",
								transform: "translateY(-50%)"
							}}
						/>

						{/* Animated Oval with Drag Grip Lines wrapped */}
						<Tooltip
							hidden={isMovingRef.current}
							label={(Math.abs(initialPercentage - topPercentage) < 5) ? ("Drag Library Pane") : ("Click to Reset")}
							openDelay={200}
							withArrow
						>
							<Box
								style={{
									position: "absolute",
									top: "50%",
									left: hoverX - 150, // Center the 300px oval on the mouse position
									transform: "translateY(-50%)",
									width: "300px",
									height: isHovered ? 12 : 0,
									backgroundColor: "#ccc",
									borderRadius: "4px",
									opacity: isHovered ? 1 : 0,
									transition:
										isHovered
											? "height 50ms ease-in 200ms, opacity 10ms ease-in 200ms"
											: isMovingRef.current
												? "height 100ms ease-out 500ms, opacity 100ms ease-out 550ms"
												: "height 100ms ease-out, opacity 100ms ease-out 50ms",
									display: "flex",
									flexDirection: "column",
									justifyContent: "space-around",
									alignItems: "center",
									outline: "1px solid #888"
								}}
							>
								<Box
									style={{
										width: "10%",
										height: "2px",
										backgroundColor: "#888",
										borderRadius: "1px",
									}}
								/>
								<Box
									style={{
										width: "10%",
										height: "2px",
										backgroundColor: "#888",
										borderRadius: "1px",
									}}
								/>
								<Box
									style={{
										width: "10%",
										height: "2px",
										backgroundColor: "#888",
										borderRadius: "1px",
									}}
								/>
							</Box>
						</Tooltip>
					</Box>

					{/* Bottom Pane */}
					<Box
						style={{
							position: "relative",
							top: -interactHeight,
							overflow: "hidden",
							transition: isMovingRef.current ? "none" : "all 200ms ease-out",
							height: `${100 - topPercentage}%`,
						}}
					>
						{children[1]}
					</Box>
				</>
			) : (
				/* Bottom Pane */
				<Box
					style={{
						position: "relative",
						top: -interactHeight,
						overflow: "hidden",
						transition: isMovingRef.current ? "none" : "all 200ms ease-out",
						height: "100%"
					}}
				>
					{children[1]}
				</Box>
			)}
		</Box>
	);
};

export default ResizablePane;
